/**
 * Recents — last-N-visited admin route tracker.
 *
 * Per-tenant scoped via the localStorage key suffix. A super_admin
 * who tenant-switches sees a different recents list per tenant
 * (a route that exists in tenant A might 404 in tenant B, so
 * conflating contexts is wrong UX).
 *
 * Storage shape: a JSON array of RecentEntry, head = most recent,
 * capped at MAX_ENTRIES. Visiting an href that's already in the
 * list bumps it to the head; visiting a new href prepends and
 * trims.
 *
 * Failure modes (each fails-soft, never throws to the caller):
 *   - localStorage quota exceeded → write skipped, read still works
 *   - localStorage disabled (private mode) → returns []
 *   - JSON parse error (corrupt entry) → returns [], next push
 *     overwrites
 *   - Missing key → returns []
 */

export interface RecentEntry {
  href: string;
  label: string;
  /** Epoch ms. Stored for future use (e.g., relative-time display
   *  in a polish ticket); not rendered in v1. Order alone
   *  communicates recency. */
  visited_at: number;
}

const STORAGE_KEY_PREFIX = 'evi.admin.nav.recents.v1.';
const MAX_ENTRIES = 10;

function keyFor(tenantId: string): string {
  return `${STORAGE_KEY_PREFIX}${tenantId}`;
}

/**
 * Read the recents list for a tenant. Returns [] on any failure
 * (parse error, missing key, localStorage unavailable). Never
 * throws.
 */
export function getRecents(tenantId: string): RecentEntry[] {
  if (!tenantId) return [];
  try {
    const raw = localStorage.getItem(keyFor(tenantId));
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive validation — drop entries that don't match the
    // expected shape rather than rejecting the whole list. A
    // single corrupt entry shouldn't blank the list.
    return parsed.filter(isValidEntry).slice(0, MAX_ENTRIES);
  } catch {
    return [];
  }
}

function isValidEntry(value: unknown): value is RecentEntry {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['href'] === 'string'
    && typeof v['label'] === 'string'
    && typeof v['visited_at'] === 'number'
  );
}

/**
 * Push a new entry. Bumps to head if the href already exists
 * (dedup by href, regardless of label drift — a label change for
 * the same route is a label refresh, not a new visit). Caps at
 * MAX_ENTRIES. Fails-soft on storage errors.
 */
export function pushRecent(tenantId: string, entry: RecentEntry): void {
  if (!tenantId) return;
  try {
    const current = getRecents(tenantId);
    const filtered = current.filter((e) => e.href !== entry.href);
    const next = [entry, ...filtered].slice(0, MAX_ENTRIES);
    localStorage.setItem(keyFor(tenantId), JSON.stringify(next));
  } catch {
    // Quota exceeded / private mode / etc. Recents are best-
    // effort UX; failure here just means the new visit doesn't
    // record. The next pushRecent attempt may succeed (e.g., if
    // the user clears storage).
  }
}

/**
 * Clear the recents list for a tenant. Fails-soft.
 */
export function clearRecents(tenantId: string): void {
  if (!tenantId) return;
  try {
    localStorage.removeItem(keyFor(tenantId));
  } catch {
    /* ignore */
  }
}
